import { useEffect, useRef, useState } from 'react'

// A card scrolled out of the panel is still in the document, and a GIF in the
// document keeps decoding frames, so the observer has to report leaving as well
// as entering - the caller unmounts the preview on the way out. The margin keeps
// the swap ahead of the scroll so a card is never blank once it is on screen.
const OBSERVER_OPTIONS = { rootMargin: '100px', threshold: 0 }

/**
 * Tracks whether the returned ref's element is within the viewport.
 *
 * @returns {[React.RefObject, boolean]} The element ref and its visibility.
 */
export default function useIsVisible() {
  const ref = useRef(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const element = ref.current

    // SSR and jsdom have no observer; showing the content is the safe fallback.
    if (!element || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true)

      return undefined
    }

    const observer = new IntersectionObserver(([entry]) => {
      setIsVisible(entry.isIntersecting)
    }, OBSERVER_OPTIONS)

    observer.observe(element)

    return () => observer.disconnect()
  }, [])

  return [ref, isVisible]
}
